import { useState, useEffect, useMemo } from 'react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { getAutomationsForRecordGroup } from '@/services/api/automation.service'
import { getDashboards } from '@/services/api/dashboard.service'
import { getAllForms } from '@/services/api/form.service'
import { getRoles } from '@/services/api/permission.service'
import { searchRecords } from '@/services/api/record.service'
import { getTags } from '@/services/api/tag.service'
import type { CatalogAction, CatalogResource, PermissionScope } from '@/types/api/permission'
import type { RecordListItem } from '@/types/api/record'
import type { RecordGroup } from '@/types/api/record-group'
import { cn } from '@/utils/cn'
import { logger } from '@/utils/logger'

import { RecordGroupTreePicker } from './RecordGroupTreePicker'

export interface ScopedGrantOption {
  action: CatalogAction
  resource: CatalogResource
}

export interface ScopedGrantInput {
  permissionKey: string
  resourceType: string
  resourceId: string
  scope: PermissionScope
}

interface AddResourcePermissionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Grantable actions (from the permission catalog), usually one tab's worth. */
  options: ScopedGrantOption[]
  recordGroups: RecordGroup[]
  onSave: (data: ScopedGrantInput) => void
  isSaving?: boolean
}

type Mode = 'ITEM' | 'GROUP'

/** Resource types without a record group whose items can be picked from a flat list. */
const STANDALONE_PICKERS: Record<string, () => Promise<Array<{ id: string; label: string }>>> = {
  DASHBOARD: async () =>
    (await getDashboards()).flatMap((d) => (d.id ? [{ id: d.id, label: d.title }] : [])),
  ROLE: async () => (await getRoles()).map((r) => ({ id: r.id, label: r.name })),
}

/** Records are picked from a server search (first 100 matches), not a full list. */
const RECORD_PICKER_LIMIT = 100

/** Search rows carry the title, not field values (see RecordsTable), so fall back to a short id. */
function recordLabel(record: RecordListItem) {
  return record.title?.trim() || `Untitled record ${record.recordId.substring(0, 8)}`
}

/** Modes an action supports, derived from the scopes the backend allows for it. */
function modesFor(option: ScopedGrantOption): Mode[] {
  const { action, resource } = option
  const modes: Mode[] = []
  const pickable = resource.hierarchy !== 'STANDALONE' || resource.resourceType in STANDALONE_PICKERS
  if (action.scopes.includes('NODE') && pickable) modes.push('ITEM')
  if (action.scopes.includes('GROUP_NODE')) modes.push('GROUP')
  return modes
}

/**
 * Grants a permission on specific items: one resource, one record group (optionally with
 * sub-groups), or everything inside a record group. Workspace-wide grants are the toggles
 * on the permission tabs.
 */
export function AddResourcePermissionDialog({
  open,
  onOpenChange,
  options: allOptions,
  recordGroups,
  onSave,
  isSaving = false,
}: AddResourcePermissionDialogProps) {
  const options = useMemo(() => allOptions.filter((o) => modesFor(o).length > 0), [allOptions])

  const [selectedKey, setSelectedKey] = useState('')
  const [mode, setMode] = useState<Mode>('ITEM')
  const [includeSubtree, setIncludeSubtree] = useState(false)
  const [groupId, setGroupId] = useState<string | null>(null)
  const [itemId, setItemId] = useState('')
  const [items, setItems] = useState<Array<{ id: string; label: string }>>([])
  const [itemsLoading, setItemsLoading] = useState(false)
  const [itemQuery, setItemQuery] = useState('')
  const debouncedItemQuery = useDebouncedValue(itemQuery, 300)

  const option = options.find((o) => o.action.key === selectedKey) ?? options[0]
  const resourceType = option?.resource.resourceType
  const hierarchy = option?.resource.hierarchy
  const modes = option ? modesFor(option) : []

  useEffect(() => {
    if (open) {
      const first = options[0]
      setSelectedKey(first?.action.key ?? '')
      setMode(first ? modesFor(first)[0] : 'ITEM')
      setIncludeSubtree(false)
      setGroupId(null)
      setItemId('')
      setItems([])
      setItemQuery('')
    }
  }, [open, options])

  // Load pickable items: standalone lists, or the members of the chosen record group.
  useEffect(() => {
    if (!open || !option || mode !== 'ITEM' || hierarchy === 'RECORD_GROUP_TREE') return
    let cancelled = false
    const load = async (): Promise<Array<{ id: string; label: string }>> => {
      if (hierarchy === 'STANDALONE') return STANDALONE_PICKERS[resourceType!]?.() ?? []
      if (!groupId) return []
      switch (resourceType) {
        case 'FORM':
          return (await getAllForms(groupId)).map((f) => ({ id: f.id, label: `Version ${f.version}` }))
        case 'AUTOMATION':
          return (await getAutomationsForRecordGroup(groupId)).map((a) => ({ id: a.id, label: `Trigger: ${a.triggerEvent}` }))
        case 'TAG': {
          // Tags of every type in the group; restricted ones first, since only they need tag:use.
          const types = [['RECORD', 'Record'], ['ACTIVITY', 'Activity'], ['FOLLOWUP', 'Follow-up']] as const
          const lists = await Promise.all(types.map(([type]) => getTags(groupId, type)))
          return lists
            .flatMap((tags, i) => tags.map((t) => ({ tag: t, kind: types[i][1] })))
            .sort((a, b) => Number(b.tag.assignmentMode === 'RESTRICTED') - Number(a.tag.assignmentMode === 'RESTRICTED'))
            .map(({ tag, kind }) => ({
              id: tag.id,
              label: `${kind} · ${tag.label}${tag.assignmentMode === 'RESTRICTED' ? ' (restricted)' : ''}`,
            }))
        }
        case 'RECORD':
        case 'RECORD_ACTIVITY':
          return (await searchRecords(groupId, {
            q: debouncedItemQuery || undefined,
            sort: { by: 'createdAt', dir: 'DESC' },
            page: 0,
            size: RECORD_PICKER_LIMIT,
          })).content.map((r) => ({ id: r.recordId, label: recordLabel(r) }))
        default:
          return []
      }
    }
    setItemsLoading(true)
    setItemId('')
    load()
      .then((list) => {
        if (cancelled) return
        setItems(list)
        if (list.length > 0) setItemId(list[0].id)
      })
      .catch((err) => logger.error('Failed to load items for permission picker', err, 'expected'))
      .finally(() => !cancelled && setItemsLoading(false))
    return () => {
      cancelled = true
    }
  }, [open, option, mode, hierarchy, resourceType, groupId, debouncedItemQuery])

  const handleSave = () => {
    if (!option) return
    const supportsSubtree = (scope: PermissionScope) => option.action.scopes.includes(scope)
    if (mode === 'GROUP') {
      onSave({
        permissionKey: option.action.key,
        resourceType: resourceType!,
        resourceId: groupId!,
        scope: includeSubtree && supportsSubtree('GROUP_SUBTREE') ? 'GROUP_SUBTREE' : 'GROUP_NODE',
      })
    } else if (hierarchy === 'RECORD_GROUP_TREE') {
      onSave({
        permissionKey: option.action.key,
        resourceType: resourceType!,
        resourceId: groupId!,
        scope: includeSubtree && supportsSubtree('SUBTREE') ? 'SUBTREE' : 'NODE',
      })
    } else {
      onSave({ permissionKey: option.action.key, resourceType: resourceType!, resourceId: itemId, scope: 'NODE' })
    }
  }

  const needsGroup = mode === 'GROUP' || hierarchy !== 'STANDALONE'
  const isSaveDisabled = !option || (needsGroup && !groupId)
    || (mode === 'ITEM' && hierarchy !== 'RECORD_GROUP_TREE' && !itemId)
  const showSubtree = mode === 'GROUP' ? option?.action.scopes.includes('GROUP_SUBTREE')
    : hierarchy === 'RECORD_GROUP_TREE' && option?.action.scopes.includes('SUBTREE')

  const selectClass = 'w-full rounded-lg border border-neutral-200 bg-white p-2 text-sm outline-none focus:border-primary-500'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] w-[calc(100%-2rem)] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add access to specific items</DialogTitle>
          <DialogDescription>
            Grant a permission on one item, a record group, or everything inside a record group.
          </DialogDescription>
        </DialogHeader>

        {options.length === 0 ? (
          <p className="text-sm text-neutral-500">Nothing in this area can be granted on specific items.</p>
        ) : (
          <div className="my-2 space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-neutral-600">Permission</Label>
              <select
                value={option?.action.key}
                onChange={(e) => {
                  const next = options.find((o) => o.action.key === e.target.value)
                  setSelectedKey(e.target.value)
                  setMode(next ? modesFor(next)[0] : 'ITEM')
                  setGroupId(null)
                  setItemId('')
                }}
                className={selectClass}
              >
                {options.map((o) => (
                  <option key={o.action.key} value={o.action.key}>
                    {o.resource.label}: {o.action.label}
                  </option>
                ))}
              </select>
              {option && <p className="text-xs text-neutral-500">{option.action.description}</p>}
            </div>

            {modes.length > 1 && (
              <div className="grid grid-cols-2 gap-2">
                {modes.map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => {
                      setMode(m)
                      setItemId('')
                    }}
                    className={cn(
                      'rounded-lg border px-3 py-2 text-xs font-medium transition-colors',
                      mode === m
                        ? 'border-primary-300 bg-primary-50 text-primary-700'
                        : 'border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50'
                    )}
                  >
                    {m === 'ITEM' ? 'A specific item' : 'Everything in a record group'}
                  </button>
                ))}
              </div>
            )}

            {needsGroup && (
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-neutral-600">Record group</Label>
                <RecordGroupTreePicker recordGroups={recordGroups} selectedId={groupId} onSelect={setGroupId} />
              </div>
            )}

            {showSubtree && (
              <div className="flex items-center justify-between gap-3 rounded-lg border border-neutral-200 bg-neutral-50/50 p-3">
                <div className="space-y-0.5">
                  <Label className="text-xs font-semibold text-neutral-700">Include sub-groups</Label>
                  <p className="text-[11px] text-neutral-500">Also applies to every descendant record group.</p>
                </div>
                <Switch checked={includeSubtree} onCheckedChange={setIncludeSubtree} />
              </div>
            )}

            {mode === 'ITEM' && hierarchy !== 'RECORD_GROUP_TREE' && (hierarchy === 'STANDALONE' || groupId) && (
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-neutral-600">{option?.resource.label}</Label>
                {(resourceType === 'RECORD' || resourceType === 'RECORD_ACTIVITY') && (
                  <Input
                    type="search"
                    className="h-9"
                    placeholder="Search records…"
                    aria-label="Search records"
                    value={itemQuery}
                    onChange={(e) => setItemQuery(e.target.value)}
                  />
                )}
                {itemsLoading ? (
                  <p className="py-2 text-xs text-neutral-500">Loading…</p>
                ) : (
                  <select value={itemId} onChange={(e) => setItemId(e.target.value)} className={selectClass}>
                    {items.length === 0 && <option value="">Nothing to choose from</option>}
                    {items.map((item) => (
                      <option key={item.id} value={item.id}>{item.label}</option>
                    ))}
                  </select>
                )}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="mt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaveDisabled || isSaving}>
            {isSaving ? 'Granting…' : 'Grant access'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
